import {
  Component,
  InputSignal,
  OutputEmitterRef,
  Signal,
  computed,
  inject,
  input,
  output
} from '@angular/core';

import { LinkAccessibilityDirective } from '../../directives/link-accessibility/link-accessibility.directive';
import { OverlayComponent } from '../overlay/overlay.component';
import { OverlayHeaderComponent } from '../overlay/overlay-header.component';
import { OverlayToggleDirective } from '../overlay/overlay-toggle.directive';
import { IconComponent } from '../icon/icon.component';
import { OverlayService } from '../overlay/overlay.service';
import { DividerComponent } from '../panel/divider.component';

export type StepTrackerType = 'indeterminate' | 'determinate' | 'percentage';

/**
 * StepTrackerComponent
 * Implements Emerald standard view of steps component
 * The component is simplified to
 */

import { TranslatePipe } from '../../pipes/translate.pipe';

@Component({
  selector: 'td-wb-step-tracker',
  templateUrl: 'step-tracker.component.html',
  styleUrls: ['step-tracker.component.scss'],
  imports: [
    LinkAccessibilityDirective,
    OverlayComponent,
    OverlayHeaderComponent,
    OverlayToggleDirective,
    IconComponent,
    DividerComponent
    ,
    TranslatePipe]
})
export class StepTrackerComponent {

  readonly type: InputSignal<StepTrackerType> = input<StepTrackerType>('indeterminate');
  readonly editable: InputSignal<boolean> = input<boolean>(false);

  /**
   * percentage
   * Meant to indicate the current percentage completed of the entire process when using percentage type
   * or the percentage completed of the current step when using determinate type.
   */
  readonly percentage: InputSignal<number> = input<number>(0);

  /**
   * steps
   * An array of strings that represent each step of the page
   * Example: ['step1', 'step2', 'step3'] or ['enter', 'review', 'complete']
   * The text displayed will be the item text (ie 'step1') unless consumer sets translateNamespace,
   * in which case it will be the 'translateNamespace'+'.'+item (ie 'tdWb.example.step1')
   */
  readonly steps: InputSignal<Array<string> | undefined> = input<Array<string> | undefined>(undefined);

  /**
   * translateNamespace
   * A fully qualified translation path for the page
   * Or empty, if the steps label will be displayed directly on screen
   * Example: 'tdWb.home.summary'
   */
  readonly translateNamespace: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * currentStep
   * The 0-based index of the current step to display in the steps array
   */
  readonly currentStep: InputSignal<number> = input<number>(0);

  readonly onStepSelected: OutputEmitterRef<string> = output<string>();

  readonly overlayService = inject(OverlayService);

  private readonly validatedCurrentStep: Signal<number> = computed(() => {
    const newIndex = this.currentStep();
    const steps = this.steps();
    if (steps && !this.isValidIndex(newIndex, steps)) {
      throw new Error(`Invalid value for step: ${newIndex}`);
    }
    return newIndex;
  });

  readonly stepLabel: Signal<string> = computed(() => {
    const steps = this.steps();
    return (steps?.length ?? 0) > this.validatedCurrentStep() ? this.getStepText() : '';
  });

  readonly currentStepNumber: Signal<number> = computed(() => {
    return this.validatedCurrentStep() + 1;
  });

  readonly stepTotal: Signal<number> = computed(() => {
    return this.steps()?.length ?? 0;
  });

  readonly showStepTracker: Signal<boolean> = computed(() => {
    this.validatedCurrentStep();
    return this.stepTotal() > 0 || (this.type() === 'percentage' && !this.editable());
  });

  getSpecificStepText(index: number): string {
    const ns = this.translateNamespace();
    const steps = this.steps();
    if (ns !== undefined && steps?.[index] !== undefined) {
      return `${ns}.${steps[index]}`;
    }
    return steps?.[index] ?? '';
  }

  getIconType(index: number): 'primary' | 'primary2' | 'inverse' | 'secondary' | undefined {
    return index < this.validatedCurrentStep() ? 'primary' : undefined;
  }

  stepSelected(index: number): void {
    const steps = this.steps();
    if (index >= 0 && index < (steps?.length ?? 0)) {
      const step = steps?.[index];
      if (step !== undefined) {
        this.onStepSelected.emit(step);
        this.overlayService.close();
      }
    }
  }

  private isValidIndex(index: number, steps: Array<string | undefined>): boolean {
    return steps !== undefined && index > -1 && index < steps.length;
  }

  private getStepText(): string{
    const translateNamespace = this.translateNamespace();
    const steps = this.steps();
    const currentStep = this.validatedCurrentStep();
    if (translateNamespace !== undefined && steps?.[currentStep] !== undefined) {
      return `${translateNamespace}.${steps[currentStep]}`;
    }
    return (steps?.[currentStep] ?? '');
  }
}
